/**
 * Contact utilities for name resolution and formatting
 */
import { UNKNOWN_CONTACT_NAME } from '../constants/contacts.js';
/**
 * Split a full name into firstName and lastName.
 * First word becomes firstName, remaining words become lastName.
 *
 * @param fullName - Full name string
 * @returns Object with firstName and lastName
 *
 * @example
 * splitName("John Doe")        // { firstName: "John", lastName: "Doe" }
 * splitName("John Smith Jr.")  // { firstName: "John", lastName: "Smith Jr." }
 * splitName("Madonna")         // { firstName: "Madonna", lastName: "" }
 * splitName("")                // { firstName: "", lastName: "" }
 */
export function splitName(fullName) {
    if (!fullName?.trim()) {
        return { firstName: '', lastName: '' };
    }
    const parts = fullName.trim().split(/\s+/);
    if (parts.length === 1) {
        return { firstName: parts[0], lastName: '' };
    }
    return { firstName: parts[0], lastName: parts.slice(1).join(' ') };
}
// Treat blank/whitespace and stringified placeholders ("null"/"undefined") as absent
function cleanNameField(value) {
    const trimmed = value?.trim();
    if (!trimmed) {
        return undefined;
    }
    const lowered = trimmed.toLowerCase();
    return lowered === 'null' || lowered === 'undefined' ? undefined : trimmed;
}
/**
 * Resolve contact name from contact object
 * Attempts to build a full name from firstName/lastName, falls back to name field, or returns default
 *
 * @param contact - Contact object with name fields
 * @param defaultName - Default name if no name fields available
 * @returns Resolved contact name
 *
 * @example
 * resolveContactName({ firstName: "John", lastName: "Doe" })
 * // Returns: "John Doe"
 *
 * resolveContactName({ firstName: "John" })
 * // Returns: "John"
 *
 * resolveContactName({ name: "John Doe" })
 * // Returns: "John Doe"
 *
 * resolveContactName({})
 * // Returns: "Unknown Contact"
 */
export function resolveContactName(contact, defaultName = UNKNOWN_CONTACT_NAME) {
    if (!contact) {
        return defaultName;
    }
    const firstName = cleanNameField(contact.firstName);
    const lastName = cleanNameField(contact.lastName);
    const name = cleanNameField(contact.name);
    if (firstName && lastName) {
        return `${firstName} ${lastName}`;
    }
    if (firstName) {
        return firstName;
    }
    if (lastName) {
        return lastName;
    }
    if (name) {
        return name;
    }
    return defaultName;
}
/**
 * Format contact name for display in different contexts
 *
 * @param contact - Contact object with name fields
 * @param format - Display format ('full' | 'first' | 'last' | 'initials')
 * @param defaultName - Default name if no name fields available
 * @returns Formatted contact name
 *
 * @example
 * formatContactName({ firstName: "John", lastName: "Doe" }, 'initials')
 * // Returns: "J.D."
 *
 * formatContactName({ firstName: "John", lastName: "Doe" }, 'first')
 * // Returns: "John"
 */
export function formatContactName(contact, format = 'full', defaultName = UNKNOWN_CONTACT_NAME) {
    if (!contact) {
        return defaultName;
    }
    const { firstName, lastName, name } = contact;
    switch (format) {
        case 'first':
            return firstName || name || defaultName;
        case 'last':
            return lastName || name || defaultName;
        case 'initials':
            if (firstName && lastName) {
                return `${firstName[0]}.${lastName[0]}.`;
            }
            if (firstName) {
                return `${firstName[0]}.`;
            }
            if (lastName) {
                return `${lastName[0]}.`;
            }
            if (name && name.length > 0) {
                const nameParts = name.split(' ');
                if (nameParts.length >= 2) {
                    return `${nameParts[0][0]}.${nameParts[1][0]}.`;
                }
                return `${name[0]}.`;
            }
            return defaultName;
        case 'full':
        default:
            return resolveContactName(contact, defaultName);
    }
}
/**
 * Resolve a contact's display-name parts when ingesting PMS/CSV contact data.
 * When both name parts are absent, fall back to `companyName` — the CONTACT'S
 * business/entity name (e.g. an LLC that owns properties, stored on
 * `contact.companyName`), NOT our platform Company (the property-management
 * business that is our direct customer). Last resort: "Unknown Contact".
 */
export function resolveNameParts(firstName, lastName, companyName) {
    const first = firstName || '';
    const last = lastName || '';
    if (!first && !last) {
        if (companyName)
            return { firstName: companyName, lastName: '' };
        return splitName(UNKNOWN_CONTACT_NAME);
    }
    return { firstName: first, lastName: last };
}
/**
 * Returns true when the name exactly matches the synthetic Unknown Contact placeholder.
 * Use this before merging CSV data so placeholder values don't bleed into real names.
 */
export function isUnknownContactPlaceholder(firstName, lastName) {
    const { firstName: placeholderFirst, lastName: placeholderLast } = splitName(UNKNOWN_CONTACT_NAME);
    return firstName === placeholderFirst && lastName === placeholderLast;
}
/**
 * Capitalize first letter and lowercase the rest for group type names
 * @param name - Group type name (e.g., "RESIDENT", "VENDOR")
 * @returns Formatted name (e.g., "Resident", "Vendor")
 */
export function formatGroupTypeName(name) {
    return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
}
/**
 * Format a list of contact groups into a comma-separated display string
 * @param contactGroups - Array of contact group objects with groupType or customGroup
 * @returns Comma-separated list of group names, or 'Unknown group' if empty
 *
 * @example
 * formatContactGroupList([{ groupType: 'RESIDENT', customGroup: null }])
 * // Returns: "Resident"
 *
 * @example
 * formatContactGroupList([{ groupType: null, customGroup: 'VIP Tenant' }])
 * // Returns: "VIP Tenant"
 *
 * @example
 * formatContactGroupList([
 *   { groupType: 'VENDOR', customGroup: null },
 *   { groupType: null, customGroup: 'Board Member' }
 * ])
 * // Returns: "Vendor, Board Member"
 */
export function formatContactGroupList(contactGroups) {
    const groupList = contactGroups
        .map(g => {
        if (g.groupType)
            return formatGroupTypeName(g.groupType);
        if (g.customGroup)
            return g.customGroup; // Custom groups already have proper casing
        return null;
    })
        .filter(Boolean)
        .join(', ');
    return groupList || 'Unknown group';
}
//# sourceMappingURL=contact-utils.js.map